/**
 * The one thing in the app that opens a camera (CAM-01, architecture §23; PRV-02).
 *
 * **Why it is a single long-lived object.** Two facts from the Slice 0 spike shape this design.
 * The first GPU inference costs ~7.3 s (D-060(3)), so the pose engine must be built once and kept
 * warm for the whole sitting — a session that closed its engine when a screen unmounted would make
 * a child wait again on every visit. And exactly one thing may own the `MediaStream`, or
 * "stop the tracks on exit" has two owners and therefore none.
 *
 * So: `start()` may be called any number of times, `stop()` ends the tracks and leaves the engine
 * warm, and `dispose()` — the end of the sitting — is the only thing that releases the engine.
 *
 * **What it does not do.** No detection loop for gameplay (Slice B), no semantic events, no
 * calibration, and nothing persisted anywhere: every reading is computed per frame and dropped.
 * The engine sees the video element; nothing above this file sees a frame or a landmark.
 */

import { DEFAULT_GAMEPLAY_TUNABLES } from '../../platform/config/tunables';
import type { PoseTunables } from '../../shared/types/tunables';
import { createCalibrationStore, type CalibrationStore } from '../calibration/calibration-store';
import type { PoseFrame, PoseTrackerHealth } from '../pose';
import { isLowPerformance, meanFrameTime, recordFrameTime } from '../pose/frame-performance';
import { CooperativeReadingsGate, cooperativeObservationFrom } from './coop-readings';
import { bodyReadingsFrom, meanLuminanceFromRgba } from './frame-readings';
import type { BodyReadings, CooperativeReadings } from './readiness';
import type { CameraMode } from '../../shared/types/camera-mode';
import type { CameraPermissionState, CameraReadings, PoseEngineReadiness } from './readiness';

/** The part of `navigator.mediaDevices` this session uses. */
export interface CameraMediaDevices {
  getUserMedia(constraints: MediaStreamConstraints): Promise<MediaStream>;
  enumerateDevices(): Promise<MediaDeviceInfo[]>;
}

/** What an engine has to be built to look for. */
export interface PoseEngineRequest {
  /**
   * How many bodies detection may return (`numPoses`).
   *
   * One unless a cooperative sitting asked for two: the Slice 0′ spike measured two-pose detection
   * at 1.9× the cost of one, and below the frame-rate floor on CPU (D-077(1)). A solo child does not
   * pay for a body nobody is looking for.
   */
  readonly maxPoses: number;
}

/** The part of the pose engine this session uses, so tests need no WASM runtime. */
export interface PoseEngineLike {
  initialise(): Promise<unknown>;
  detectVideoFrame(source: HTMLVideoElement, timestampMs: number): PoseFrame;
  close(): void;
}

export type CameraSessionStatus = 'idle' | 'starting' | 'live' | 'stopped' | 'blocked';

export interface CameraSessionSnapshot {
  readonly status: CameraSessionStatus;
  /**
   * Whether a stream is open right now. This is what the camera-active indicator renders from —
   * it is derived from the tracks themselves, never set optimistically.
   */
  readonly streamActive: boolean;
  /** Numbers only. No frame, no landmark, nothing that could describe a child. */
  readonly readings: CameraReadings;
  /** The cold first inference has been paid (D-060(3)). */
  readonly warmedUp: boolean;
}

export interface CameraSessionOptions {
  /** Defaults to `navigator.mediaDevices` when the browser has one. */
  readonly mediaDevices?: CameraMediaDevices | null;
  /** Defaults to a dynamically imported {@link PoseLandmarkerEngine}. */
  readonly createEngine?: (request: PoseEngineRequest) => Promise<PoseEngineLike>;
  /** Defaults to drawing the frame to a small canvas and averaging it. */
  readonly sampleLuminance?: (video: HTMLVideoElement) => number | null;
  /** Defaults to `setTimeout` at the target cadence. Returns a canceller. */
  readonly schedule?: (callback: () => void, delayMs: number) => () => void;
  /**
   * Defaults to `setInterval`. The frame watchdog's own scheduler, kept separate from
   * {@link CameraSessionOptions.schedule} because it must keep running in exactly the situation the
   * sampling loop has stopped producing anything (see {@link CameraSession.frameStarvation}).
   */
  readonly starvationSchedule?: (callback: () => void, intervalMs: number) => () => void;
  /**
   * How often readings are refreshed, in ms. Defaults to the `poseTargetFps` cadence; the shell
   * passes the slower readiness cadence, because a checklist is not gameplay (see
   * `CAMERA_READINESS_THRESHOLDS.sampleIntervalMs`).
   */
  readonly sampleIntervalMs?: number;
  readonly pose?: PoseTunables;
  readonly now?: () => number;
}

/**
 * How long frames have been missing — the "camera disconnected" signal (GDS §36, D-065(4)).
 *
 * A duration and not a verdict: this object owns the stream and therefore the only clock that keeps
 * running when the frames stop, but what a family should see about it is the recovery monitor's
 * decision, exactly as tracking loss is.
 */
export interface CameraStarvationState {
  readonly starved: boolean;
  /** Milliseconds since the last frame arrived, or 0 while frames are flowing. */
  readonly starvedForMs: number;
}

/**
 * Where the starvation signal is read. Structural, so the recovery monitor depends on a shape
 * rather than on the camera session itself.
 */
export interface CameraStarvationSource {
  subscribe(listener: (state: CameraStarvationState) => void): () => void;
  readonly state: CameraStarvationState;
}

const NOT_STARVED: CameraStarvationState = { starved: false, starvedForMs: 0 };

const IDLE_READINGS: CameraReadings = {
  browserSupported: true,
  cameraAvailable: null,
  permission: 'unknown',
  poseEngine: 'idle',
  meanLuminance: null,
  body: null,
  cooperative: null,
  frameTimeMs: null,
};

/** What a sitting looks for unless it says otherwise. Phase 3's shipped `numPoses`. */
const SINGLE_BODY = 1;

export class CameraSession {
  private readonly options: CameraSessionOptions;
  private readonly pose: PoseTunables;
  private readonly now: () => number;

  /**
   * This sitting's calibrations (GDS §13 "store session-only", architecture §20).
   *
   * It hangs off the session because the session *is* the sitting: it owns the stream and the warm
   * engine, and `dispose()` — the end of the sitting — is where a child's measurements are
   * forgotten. There is no repository and no `thrive.v1.*` key, deliberately; persisting a
   * calibration would be new, un-reviewed storage ground (recon storage watch, D-031).
   */
  readonly calibration: CalibrationStore = createCalibrationStore();

  /**
   * What the readiness check last suggested about this room (D-059, GDS §6's closing line).
   *
   * It hangs off the session for the same reason the calibrations do: it is a fact about *this*
   * sitting, in this room, with this child standing where they were standing — not a preference
   * about who they are. Session-only, no `thrive.v1.*` key, gone at `dispose()`. Parent Setup reads
   * it once to seed its field; the parent's answer is what actually decides anything.
   *
   * It keeps the **last measured** recommendation rather than the current reading, and that is
   * deliberate: a grown-up walks from the readiness screen to the setup screen with the child in
   * tow, so by the time the field is rendered the room is empty and the live reading is `null`.
   * Forgetting on an empty room would mean the recommendation never survived to be used.
   */
  private lastRecommendedMode: CameraMode | null = null;

  private listeners = new Set<() => void>();
  private frameListeners = new Set<(frame: PoseFrame) => void>();
  private sampleIntervalOverrideMs: number | null = null;
  /** Unsubscribes the injected frame source, when one is attached. See {@link setFrameSourceOverride}. */
  private frameSourceOverride: (() => void) | null = null;
  private state: CameraSessionSnapshot = {
    status: 'idle',
    streamActive: false,
    readings: IDLE_READINGS,
    warmedUp: false,
  };

  /**
   * How many bodies this sitting is looking for, and how many the warm engine was built for.
   *
   * Two numbers rather than one because they can disagree for exactly as long as it takes to
   * rebuild: an engine is built for a count, and a sitting can change its mind when a grown-up sets
   * up a second hero. Session-only, like everything else here.
   */
  private maxPoses = SINGLE_BODY;
  private engineMaxPoses: number | null = null;

  /**
   * The one thing that knows whether two bodies are in the picture (B.2, D-075(1)).
   *
   * It absorbs the 5–7 frame second-body acquisition the spike measured (D-077(2)(d)) so nothing
   * above it ever sees a verdict flicker. Fed only while a cooperative sitting is looking for two.
   */
  private readonly cooperativeGate = new CooperativeReadingsGate();

  private video: HTMLVideoElement | null = null;
  private stream: MediaStream | null = null;
  private engine: PoseEngineLike | null = null;
  private starting: Promise<void> | null = null;
  /**
   * Which start attempt is the live one (S1-1).
   *
   * `start()` awaits the device list, the permission dialogue and the engine, and any of those can
   * outlive the screen that asked. Until this existed, a parent navigating away from the readiness
   * check while the permission prompt was open let the resolved stream install itself on the *next*
   * route — the indicator came back and the tracks stayed live on a screen nobody had asked for a
   * camera on, which is precisely the obligation PRV-02 and recon §F make ("tracks stop on every
   * exit"). `stop()` and `dispose()` bump the generation; after every await a stale attempt stops
   * whatever it acquired and mutates nothing.
   */
  private startGeneration = 0;
  private cancelTick: (() => void) | null = null;
  private frameTimes: number[] = [];
  private framesObserved = 0;
  private failedDetections = 0;
  private lastFrameAt: number | null = null;
  private disposed = false;

  /** When a frame last *arrived*, on this session's own clock — never the frame's own stamp. */
  private lastFrameArrivedAt: number | null = null;
  /**
   * The current starvation reading, in a holder rather than a plain field.
   *
   * The subscription surface below is handed out as an object with a `state` getter, and a getter
   * written in an object literal reads the literal — so it reads this holder instead, captured by
   * value. One indirection, and the alternative (aliasing `this`) is a thing the linter is right
   * about.
   */
  private readonly starvationHolder: { current: CameraStarvationState } = { current: NOT_STARVED };
  /**
   * When the camera was refused, if it was (S1-5).
   *
   * The starvation watchdog is armed by the *first frame*, which is exactly the wrong condition for
   * the case a family is least equipped to diagnose: a camera unplugged (or a permission revoked)
   * between hero training and mission launch means `getUserMedia` fails and no frame ever arrives.
   * The monitor stayed healthy, the round timers kept running, and a child answering with their
   * body was silently unobserved until the round moved on.
   *
   * A terminal camera state is therefore reported through the same duration this object already
   * publishes, so it reaches a family through GDS §36's one ladder — pause, `NO_FRAMES`, and the
   * keyboard offer once it has lasted — rather than through a second, parallel mechanism.
   */
  private blockedSince: number | null = null;
  /**
   * When the stream opened and started owing a first frame (S2-1).
   *
   * The round-1 fix armed starvation when acquisition was *refused*; this is the other half of the
   * same silence. `getUserMedia` succeeds, the stream is live and the indicator is on — and then the
   * engine fails to initialise, or every `detectVideoFrame()` throws. Nothing frame-driven can
   * notice, because the condition *is* that there are no frames, so the mission ran on with a camera
   * that had never seen anybody.
   *
   * A stream that has opened therefore owes a first frame within `frameStarvationMs` — the same
   * threshold a stream that stops mid-mission is held to, so there is one number and not two.
   * Cleared by the first frame that actually arrives, and by stopping.
   */
  private streamOpenedAt: number | null = null;

  /**
   * Which camera this sitting is actually looking through (S1-7, architecture §20's first reuse
   * rule: "reuse when camera unchanged").
   *
   * **Session-only, in memory, and never persisted** — there is no `thrive.v1.*` key for it and
   * there must not be: a device identifier is a fact about a household's hardware. It lives exactly
   * as long as the stream it was read from, so a family that closes the tab has left nothing behind.
   *
   * Until this existed, no production flow ever supplied a device id, so the otherwise-correct
   * `CAMERA_CHANGED` predicate could never fire: calibrate on the laptop's camera, unplug it, launch
   * on the external one, and a five-year-old is read against another camera's measurements.
   */
  private deviceId: string | null = null;
  private starvationListeners = new Set<(state: CameraStarvationState) => void>();
  private cancelStarvationWatch: (() => void) | null = null;

  constructor(options: CameraSessionOptions = {}) {
    this.options = options;
    this.pose = options.pose ?? DEFAULT_GAMEPLAY_TUNABLES.pose;
    this.now = options.now ?? (() => performance.now());

    // Built here rather than as a field so that the two things it needs — the listener set and the
    // reading holder — are captured directly, instead of through the session (see the holder).
    const listeners = this.starvationListeners;
    const holder = this.starvationHolder;
    this.frameStarvation = {
      subscribe(listener: (state: CameraStarvationState) => void): () => void {
        listeners.add(listener);
        return () => {
          listeners.delete(listener);
        };
      },
      get state(): CameraStarvationState {
        return holder.current;
      },
    };
  }

  get snapshot(): CameraSessionSnapshot {
    return this.state;
  }

  /**
   * Bound rather than a method: React's `useSyncExternalStore` re-subscribes whenever the function
   * identity changes, which for a 4-per-second sampler would mean tearing the subscription down
   * and rebuilding it constantly.
   */
  readonly getSnapshot = (): CameraSessionSnapshot => this.state;

  /** Whether a stream is open. A narrow selector so the indicator does not re-render on readings. */
  readonly isStreamActive = (): boolean => this.state.streamActive;

  /**
   * Which run of frames this session is on (S4-1).
   *
   * The same counter `stop()` and `dispose()` already bump to invalidate a start attempt in flight,
   * published because it is also the honest answer to a question anything holding per-frame memory
   * has to ask: *is this the stream I was following?* The frame subscription deliberately survives a
   * stop/start, so a screen that stayed attached cannot tell a new stream from the old one — and a
   * warm restart of an already-permitted camera can deliver its first frame far too quickly for a
   * clock to notice. Read, never written, from outside.
   */
  get streamGeneration(): number {
    return this.startGeneration;
  }

  /**
   * The camera the current stream came from, or null when no stream is open (S1-7).
   *
   * A scalar id and nothing else: no label, no group, no capabilities. Browsers withhold it until
   * permission is granted, which is exactly why an unknown id on either side is never treated as
   * evidence of a change (see `evaluateCalibrationReuse`).
   */
  get activeCameraDeviceId(): string | null {
    return this.deviceId;
  }

  /** The play space the readiness check last suggested for this room, or null if none has. */
  get recommendedCameraMode(): CameraMode | null {
    return this.lastRecommendedMode;
  }

  /**
   * Asks this sitting to look for `count` bodies (Slice A′, CAM-07).
   *
   * The cooperative readiness screen asks for two; nothing else asks at all, so a single-player
   * sitting keeps the `numPoses: 1` Phase 3 shipped and none of D-077(1)'s 1.9× cost.
   *
   * A warm engine built for a different count is released here rather than quietly reused: an engine
   * looking for one body would tell a family with two children in the room that it can only see one
   * of them, for ever, and no amount of standing in the right place would fix it. The next `start()`
   * builds the right one — which costs the cold first inference again (D-060(3)), and is the honest
   * price of changing what the camera is looking for.
   */
  requestMaxPoses(count: number): void {
    const next = Number.isFinite(count) ? Math.max(SINGLE_BODY, Math.round(count)) : SINGLE_BODY;
    if (this.disposed || next === this.maxPoses) return;

    this.maxPoses = next;
    this.cooperativeGate.reset();
    this.update({ readings: { ...this.state.readings, cooperative: null } });
    if (this.engine === null || this.engineMaxPoses === next) return;

    this.engine.close();
    this.engine = null;
    this.engineMaxPoses = null;
    this.update({
      warmedUp: false,
      readings: { ...this.state.readings, poseEngine: 'idle' },
    });
    // A live sitting cannot wait for the next `start()`: it is already sampling, and every tick
    // until the new engine arrives would be detection nobody asked for.
    if (this.state.streamActive) void this.prepareEngine(this.startGeneration);
  }

  /** How many bodies this sitting is looking for right now. */
  get requestedMaxPoses(): number {
    return this.maxPoses;
  }

  /**
   * Records what the readiness check measured. A `null` recommendation — nobody in frame, or a
   * distance outside both bands — leaves the last real one standing (see the field's comment).
   */
  recommendCameraMode(mode: CameraMode | null): void {
    if (mode === null || this.disposed) return;
    this.lastRecommendedMode = mode;
  }

  /**
   * How the detection loop is actually doing (CAM-06).
   *
   * `PoseTracker` has reported this shape since Slice B, but the app has no `PoseTracker` in it —
   * this session owns the one detection loop (D-063(4)), so this is where the question has to be
   * answered. Both objects stand on one shared computation (`frame-performance.ts`), so the
   * low-performance threshold cannot come to mean two different things.
   *
   * Numbers only, as everywhere else on this boundary: counts, a mean latency and a flag. Nothing
   * here describes a child.
   */
  get health(): PoseTrackerHealth {
    return {
      attached: this.state.streamActive,
      framesObserved: this.framesObserved,
      failedDetections: this.failedDetections,
      meanFrameTimeMs: meanFrameTime(this.frameTimes),
      lowPerformance: isLowPerformance(this.frameTimes, this.pose),
      lastFrameAt: this.lastFrameAt,
    };
  }

  readonly subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  };

  /**
   * The frames this session is already detecting, for anything that needs to *read* a body rather
   * than a reading — hero training, and Slice D's gameplay adapter.
   *
   * A second detection loop over the same warm engine is the thing this exists to prevent: one
   * `PoseLandmarker` in VIDEO mode is a stateful tracker with a monotonic timestamp contract, and
   * two loops driving it would interleave two children's worth of history through one filter.
   * Structurally a `PoseTracker` — anything that wants frames takes `{ subscribe }`, and either
   * source satisfies it.
   */
  readonly poseFrames = {
    subscribe: (listener: (frame: PoseFrame) => void): (() => void) => {
      this.frameListeners.add(listener);
      return () => {
        this.frameListeners.delete(listener);
      };
    },
  };

  /**
   * "Have the frames stopped altogether?" — GDS §36's *Camera disconnected* row (Slice G).
   *
   * The tracking-loss monitor runs entirely on the frame clock, deliberately (D-065(1)): a monitor
   * counting on a wall clock would keep counting through a busy machine and report a child who
   * never went anywhere. The cost of that choice, recorded honestly in Slice E and closed here, is
   * that **frames stopping entirely is invisible to it** — a cable pulled, a driver crash, a lid
   * closed on an external camera. Nothing frame-driven can notice a missing frame clock.
   *
   * So the watchdog lives here, on the object that owns the stream, and it is the one wall clock in
   * the recovery machinery. It reports a duration; the monitor decides what a family sees, so there
   * is still exactly one place that runs GDS §36's ladder.
   *
   * It watches only once frames have actually been flowing: a session nobody has started is not a
   * disconnected camera.
   */
  readonly frameStarvation: CameraStarvationSource;

  /**
   * Starts (or keeps) the watchdog running, from a frame that has just arrived.
   *
   * The poll interval is *derived* rather than a second tunable: half the starvation threshold, so
   * that starvation is noticed within one half-period of it becoming true and the duration reported
   * afterwards is fine-grained enough for the fallback offer to be earned honestly.
   */
  private noteFrameArrival(): void {
    this.lastFrameArrivedAt = this.now();
    // A frame is proof the camera opened after all, and it settles the debt the open stream owed.
    this.blockedSince = null;
    this.streamOpenedAt = null;
    this.publishStarvation(NOT_STARVED);

    this.watchForStarvation();
  }

  private checkStarvation(): void {
    if (this.blockedSince !== null) {
      this.publishStarvation({ starved: true, starvedForMs: this.now() - this.blockedSince });
      return;
    }
    // The first frame the open stream owes (S2-1). Measured from the moment the stream opened, so a
    // camera warming up is given exactly as long as a camera that stalls mid-mission.
    if (this.lastFrameArrivedAt === null && this.streamOpenedAt !== null) {
      const silentForMs = this.now() - this.streamOpenedAt;
      if (silentForMs < this.pose.frameStarvationMs) return;
      this.publishStarvation({ starved: true, starvedForMs: silentForMs });
      return;
    }
    if (this.lastFrameArrivedAt === null) return;
    const silentForMs = this.now() - this.lastFrameArrivedAt;
    if (silentForMs < this.pose.frameStarvationMs) return;
    this.publishStarvation({ starved: true, starvedForMs: silentForMs });
  }

  /**
   * The camera could not be opened at all — reported the instant it is known (S1-5).
   *
   * Immediate rather than after `frameStarvationMs`: the threshold exists to tell a stalled frame
   * loop apart from a dead one, and there is nothing to tell apart here. The watchdog is then armed
   * so the duration keeps growing, which is what earns the keyboard offer honestly.
   */
  private noteAcquisitionBlocked(): void {
    if (this.disposed || this.blockedSince !== null) return;
    this.blockedSince = this.now();
    this.publishStarvation({ starved: true, starvedForMs: 0 });
    this.watchForStarvation();
  }

  /**
   * The stream is open and owes a first frame (S2-1).
   *
   * Nothing is published here: an open camera is not yet a broken one, and saying so before the
   * deadline would put "camera trouble" on screen every time a family opened a laptop lid. The
   * watchdog is simply armed, and `checkStarvation` decides.
   */
  private noteStreamOpened(): void {
    if (this.disposed) return;
    this.streamOpenedAt = this.now();
    this.watchForStarvation();
  }

  /** Arms the one wall clock in the machinery, if it is not already running. */
  private watchForStarvation(): void {
    if (this.cancelStarvationWatch !== null || this.disposed) return;
    const schedule = this.options.starvationSchedule ?? defaultStarvationSchedule;
    const intervalMs = Math.max(1, Math.round(this.pose.frameStarvationMs / 2));
    this.cancelStarvationWatch = schedule(() => {
      this.checkStarvation();
    }, intervalMs);
  }

  private publishStarvation(next: CameraStarvationState): void {
    if (next.starved === this.starvationHolder.current.starved && next.starvedForMs === 0) return;
    this.starvationHolder.current = next;
    for (const listener of [...this.starvationListeners]) listener(next);
  }

  /**
   * Ends the watch. Nothing is announced afterwards, and nothing is left starving.
   *
   * **A stop that ends a starvation is announced (S2-2).** The camera can be restarted underneath a
   * recovery monitor that outlives it — a grown-up choosing *Restart calibration* while the frames
   * are gone does exactly that — and resetting the holder silently left that monitor believing the
   * camera was still dead, with nothing able to tell it otherwise because the source it listens to
   * had gone quiet. The parent hold then lifted on Resume and the camera hold stayed forever, which
   * is the one path where "parent override always recovers" was not true. A deliberate stop is not
   * a camera that is still broken, so whoever was told the frames had stopped is told they are no
   * longer expected.
   */
  private stopStarvationWatch(): void {
    this.cancelStarvationWatch?.();
    this.cancelStarvationWatch = null;
    this.lastFrameArrivedAt = null;
    this.blockedSince = null;
    this.streamOpenedAt = null;
    const wasStarved = this.starvationHolder.current.starved;
    this.starvationHolder.current = NOT_STARVED;
    if (wasStarved) {
      for (const listener of [...this.starvationListeners]) listener(NOT_STARVED);
    }
  }

  /**
   * Replaces the engine's frames with an injected stream — the synthetic-pose seam (Slice D).
   *
   * Chromium's fake camera shows a test pattern with **no body in it**, so no browser run can
   * observe a child answering a round with their arms and legs. This is how one does: a driver
   * gated to the dev server and a localhost preview (`syntheticPoseDriverEnabled`) publishes
   * deterministic frames here, and every consumer keeps reading the one seam it already read
   * (`poseFrames`) — the calibration flow, the gameplay adapter, all of it.
   *
   * While a source is attached the engine is **not driven at all**. That is not an optimisation:
   * one `PoseLandmarker` in VIDEO mode is a stateful tracker with a monotonic timestamp contract,
   * and interleaving a real body and a synthetic one through it is exactly the hazard D-063(4)
   * forbids. Everything else stays real — the stream, the mirrored feed, the camera-active
   * indicator, the room-brightness reading and stop-tracks-on-exit.
   */
  setFrameSourceOverride(
    source: { subscribe(listener: (frame: PoseFrame) => void): () => void } | null,
  ): void {
    this.frameSourceOverride?.();
    this.frameSourceOverride = null;
    if (source === null || this.disposed) return;

    this.frameSourceOverride = source.subscribe((frame) => {
      this.publishFrame(frame);
    });
  }

  /** Whether a synthetic source is standing in for the engine right now. */
  get usingInjectedFrames(): boolean {
    return this.frameSourceOverride !== null;
  }

  /**
   * Asks for a faster or slower sampling cadence for as long as a screen needs one.
   *
   * The readiness checklist is happy at four readings a second; hero training is not, because the
   * top of a five-year-old's jump lasts a couple of hundred milliseconds and a checklist cadence
   * would miss it entirely. `null` restores the configured interval.
   */
  setSampleIntervalMs(intervalMs: number | null): void {
    this.sampleIntervalOverrideMs =
      intervalMs === null || !Number.isFinite(intervalMs) || intervalMs <= 0 ? null : intervalMs;
  }

  /** The element the stream is shown in and the engine reads from. */
  attach(video: HTMLVideoElement | null): void {
    this.video = video;
    if (video !== null && this.stream !== null) video.srcObject = this.stream;
  }

  /**
   * Opens the camera and gets the pose engine ready.
   *
   * Concurrent callers share one attempt: React's effects can easily fire twice, and two
   * `getUserMedia` calls would light two streams of which only one could ever be stopped.
   */
  async start(): Promise<void> {
    if (this.disposed) return;
    if (this.state.streamActive) return;
    if (this.starting !== null) return this.starting;

    const generation = this.startGeneration;
    const attempt = this.run(generation).finally(() => {
      // Only the live attempt owns the slot: a stale one finishing later must not erase the
      // attempt the next screen is waiting on.
      if (this.starting === attempt) this.starting = null;
    });
    this.starting = attempt;
    return attempt;
  }

  /** Whether this attempt is still the one the session wants (S1-1). */
  private isCurrentStart(generation: number): boolean {
    return generation === this.startGeneration && !this.disposed;
  }

  private async run(generation: number): Promise<void> {
    this.update({ status: 'starting' });

    const devices = this.mediaDevices();
    if (devices === null) {
      /**
       * Deliberately NOT reported as starvation (S1-5).
       *
       * "This browser has no `mediaDevices` at all" is a hard readiness block: a sitting can never
       * reach the mission as a camera sitting in the first place, so there is no mission to hold.
       * What S1-5 is about is a camera that was *asked for and refused* at launch, which is the two
       * branches below.
       */
      this.update({
        status: 'blocked',
        readings: { ...this.state.readings, browserSupported: false, cameraAvailable: false },
      });
      return;
    }

    // Asked before prompting: a machine with no camera should be told so, not shown a permission
    // dialogue it cannot satisfy.
    const hasCamera = await this.hasCamera(devices);
    if (!this.isCurrentStart(generation)) return;
    this.update({ readings: { ...this.state.readings, cameraAvailable: hasCamera } });
    if (!hasCamera) {
      this.update({
        status: 'blocked',
        readings: { ...this.state.readings, permission: 'unavailable' },
      });
      this.noteAcquisitionBlocked();
      return;
    }

    const stream = await this.acquire(devices, generation);
    if (stream === null) return;

    if (!this.isCurrentStart(generation)) {
      // Granted after the family left. End it here and say nothing: the state belongs to whatever
      // is on screen now.
      for (const track of stream.getTracks()) track.stop();
      return;
    }

    this.stream = stream;
    this.deviceId = videoDeviceIdOf(stream);
    // From here a first frame is owed (S2-1).
    this.noteStreamOpened();
    if (this.video !== null) {
      this.video.srcObject = stream;
      play(this.video);
    }
    this.update({
      status: 'live',
      streamActive: true,
      readings: { ...this.state.readings, permission: 'granted' },
    });

    await this.prepareEngine(generation);
    if (!this.isCurrentStart(generation)) return;
    this.scheduleTick();
  }

  private mediaDevices(): CameraMediaDevices | null {
    if (this.options.mediaDevices !== undefined) return this.options.mediaDevices;
    const devices = globalThis.navigator?.mediaDevices;
    return typeof devices?.getUserMedia === 'function' ? devices : null;
  }

  private async hasCamera(devices: CameraMediaDevices): Promise<boolean> {
    try {
      const list = await devices.enumerateDevices();
      return list.some((device) => device.kind === 'videoinput');
    } catch {
      // A browser that will not enumerate can still be asked for a camera; refusing here would
      // turn a permissions quirk into "you have no camera".
      return true;
    }
  }

  private async acquire(
    devices: CameraMediaDevices,
    generation: number,
  ): Promise<MediaStream | null> {
    try {
      return await devices.getUserMedia({ video: true, audio: false });
    } catch (error) {
      // A refusal that arrives after the family left describes a screen that is gone.
      if (!this.isCurrentStart(generation)) return null;
      // Never a microphone, ever (privacy checklist #3): `audio: false` above, and nothing here
      // retries with a wider constraint.
      const name = error instanceof Error ? error.name : '';
      const permission: CameraPermissionState =
        name === 'NotFoundError' ||
        name === 'OverconstrainedError' ||
        name === 'DevicesNotFoundError'
          ? 'unavailable'
          : 'denied';

      this.update({
        status: 'blocked',
        streamActive: false,
        readings: {
          ...this.state.readings,
          permission,
          cameraAvailable:
            permission === 'unavailable' ? false : this.state.readings.cameraAvailable,
        },
      });
      this.noteAcquisitionBlocked();
      return null;
    }
  }

  /** Loads the model once and pays the cold inference here, behind the readiness checklist. */
  private async prepareEngine(generation: number): Promise<void> {
    // An engine built to look for a different number of bodies is not this sitting's engine.
    if (this.engine !== null && this.engineMaxPoses !== this.maxPoses) {
      this.engine.close();
      this.engine = null;
      this.engineMaxPoses = null;
      this.update({ warmedUp: false });
    }
    if (this.engine !== null) {
      /**
       * I-3: an attempt never completes on a cold engine (S4-4).
       *
       * The third way an engine can arrive, and the last one to be found: a stale attempt installs
       * its engine while *this* attempt is still waiting for `getUserMedia`, so by the time this one
       * reaches here the field is already occupied. Publishing `ready` and returning left
       * `warmedUp` false, and the first sampling tick paid the ~7.3 s cold inference D-060(3) exists
       * to avoid — on a screen that had done nothing wrong. Inheriting an engine is not the same as
       * having warmed one.
       */
      this.update({ readings: { ...this.state.readings, poseEngine: 'ready' } });
      this.warmUp();
      return;
    }

    this.update({ readings: { ...this.state.readings, poseEngine: 'loading' } });

    const builtFor = this.maxPoses;
    let engine: PoseEngineLike;
    try {
      engine = await (this.options.createEngine ?? defaultEngineFactory)({ maxPoses: builtFor });
      await engine.initialise();
    } catch {
      if (this.isCurrentStart(generation)) {
        this.update({ readings: { ...this.state.readings, poseEngine: 'failed' } });
      }
      return;
    }

    /**
     * The engine is the *sitting's*, not the screen's (D-060(3)) — so a warm engine that finished
     * loading after the family left is kept rather than thrown away, and a child who comes back
     * does not pay the ~7.3 s cold inference twice. What a stale attempt must not do is announce
     * readiness or start sampling for a screen that has gone.
     */
    if (this.disposed) {
      engine.close();
      return;
    }
    /**
     * The count can change **while** the model is loading (S5).
     *
     * `requestMaxPoses` releases a warm engine built for the wrong count, but a load already in
     * flight is not a field it can reach: it finds `engine === null`, has nothing to release, and
     * returns. So the arriving engine is checked against what the sitting wants *now*, exactly as it
     * is already checked against which start it belongs to. Without it a cooperative sitting whose
     * camera started before the pair was wired samples through a one-pose engine for ever, and no
     * amount of standing in the right place would find the second child.
     *
     * The generation check follows rather than precedes it: an engine built for a count nobody wants
     * is not worth keeping for the sitting under any generation, which is the one thing D-060(3)'s
     * "the engine belongs to the sitting" does not cover.
     */
    if (builtFor !== this.maxPoses) {
      engine.close();
      if (!this.isCurrentStart(generation)) return;
      /**
       * Awaited, not launched (S2-7).
       *
       * `start()` resolving means "this session is ready", and everything that waits on it acts on
       * that: the readiness screen lets a family through, the stage wires its adapters, the sampling
       * loop begins. Launching the replacement and returning resolved that promise while `engine`
       * was still null and the model still downloading, which is the readiness contract broken by
       * the fix for the count changing mid-load. The preparation this attempt still owes is part of
       * this attempt.
       */
      await this.prepareEngine(generation);
      return;
    }
    /**
     * One engine per sitting, of the *objects* and not only of the field (S2-7, D-060(3)).
     *
     * A stop and a restart can have two loads in flight at once — the round-1 rule deliberately
     * keeps a warm engine that finishes after its screen has gone, because the engine belongs to the
     * sitting. What it must not do is let the later arrival overwrite the earlier one silently: only
     * whichever was assigned last would ever be closed, and the other would keep its WASM/GPU
     * resources for the life of the tab. The one that lost the race is closed here.
     */
    if (this.engine !== null) {
      engine.close();
      /**
       * Losing the race is not the same as having no engine (S3-4).
       *
       * The abandoned attempt's engine can arrive *first* and take the field. The current attempt
       * then correctly closes its own — but returning here left the sitting at `poseEngine:
       * 'loading'` with `warmedUp: false` while a perfectly good warm engine was already installed,
       * and the first thing a child did would have paid the ~7.3 s cold inference D-060(3) exists
       * to avoid. So the retained engine is promoted and warmed for whoever is on screen now.
       */
      if (!this.isCurrentStart(generation)) return;
      this.update({ readings: { ...this.state.readings, poseEngine: 'ready' } });
      this.warmUp();
      return;
    }
    this.engine = engine;
    this.engineMaxPoses = builtFor;
    if (!this.isCurrentStart(generation)) return;

    this.update({ readings: { ...this.state.readings, poseEngine: 'ready' } });
    this.warmUp();
  }

  /**
   * Pays the cold first inference (D-060(3)) — but only against a video that can produce a frame.
   *
   * The dimension guard is not defensive tidiness, it is a defect this round created and the e2e
   * suite caught (round 4, S4-4). Warming an element whose `videoWidth` is still 0 is not a
   * warm-up: MediaPipe logs `RET_CHECK failure ... ROI width and height must be > 0` and fails the
   * graph. On every earlier caller the model download bought enough time for the element to size
   * itself; the inherited-engine path has no await at all, so it warmed microseconds after
   * `play()` — and a browser test that (rightly) fails on any unexpected `console.error` said so.
   *
   * A video that is not ready yet is therefore left alone, `warmedUp` stays false, and the debt is
   * paid by the sampling loop's first tick, which runs detection anyway. The cold inference is paid
   * once either way; what changes is that it is never paid against nothing.
   */
  private warmUp(): void {
    if (this.state.warmedUp || this.video === null || this.engine === null) return;
    if (this.video.videoWidth === 0 || this.video.videoHeight === 0) return;

    for (let index = 0; index < this.pose.poseWarmupDetections; index += 1) {
      try {
        this.engine.detectVideoFrame(this.video, this.now());
      } catch {
        /**
         * A warm-up that threw is a warm-up that did not happen (round 5, S5-3).
         *
         * It is still not worth failing readiness over — the sampling loop reports the engine's
         * real state soon enough — but recording it as success was a lie with a cost: a session
         * that stopped before its first tick and restarted skipped the eager retry, because the
         * retained-engine path saw a session that believed itself warm, and the first live tick
         * paid the cold inference after all. `warmedUp` is set by a detection that **returned**.
         */
        return;
      }
    }

    this.update({ warmedUp: true });
  }

  private scheduleTick(): void {
    if (!this.state.streamActive) return;
    const schedule = this.options.schedule ?? defaultSchedule;
    const interval =
      this.sampleIntervalOverrideMs ??
      this.options.sampleIntervalMs ??
      Math.round(1000 / this.pose.poseTargetFps);
    this.cancelTick = schedule(() => {
      this.sample();
      this.scheduleTick();
    }, interval);
  }

  /** One readiness sample: how bright the room is, and what the engine can see of the child. */
  private sample(): void {
    if (!this.state.streamActive || this.video === null) return;

    const luminance = (this.options.sampleLuminance ?? defaultLuminanceSampler)(this.video);

    let body = this.state.readings.body;
    let cooperative = this.state.readings.cooperative;
    let frameTimeMs = this.state.readings.frameTimeMs;

    // An injected stream is already producing this sitting's frames; driving the engine as well
    // would put two children through one stateful tracker (see `setFrameSourceOverride`).
    if (this.engine !== null && this.frameSourceOverride === null) {
      try {
        const frame = this.engine.detectVideoFrame(this.video, this.now());
        ({ body, cooperative } = this.applyFrame(frame));
        frameTimeMs =
          this.frameTimes.reduce((sum, value) => sum + value, 0) / this.frameTimes.length;
        /**
         * The cold inference, wherever it is actually paid (round 4, I-3).
         *
         * `warmedUp` means "a detection has succeeded on this session's engine", and the honest
         * place to learn that is a detection that succeeded. `warmUp()` pays it eagerly when it
         * can; a video that had not sized itself yet cannot be warmed against, and this is where
         * that debt is settled — on the first tick, which runs detection anyway.
         */
        if (!this.state.warmedUp) this.update({ warmedUp: true });
      } catch {
        // A single failed detection is not a dead camera; leave the last reading standing. It is
        // counted rather than swallowed, because a session made of nothing but failed detections
        // is a thing a grown-up is entitled to be told about.
        this.failedDetections += 1;
      }
    }

    this.update({
      readings: {
        ...this.state.readings,
        meanLuminance: luminance,
        body,
        cooperative,
        frameTimeMs,
      },
    });
  }

  /** One frame, from wherever it came: readings updated, then handed to the boundary's subscribers. */
  private applyFrame(frame: PoseFrame): {
    body: BodyReadings | null;
    cooperative: CooperativeReadings | null;
  } {
    const body = bodyReadingsFrom(frame, this.pose.minCoreConfidence);
    /**
     * The pair, measured only when a sitting is actually looking for two bodies.
     *
     * A one-pose engine physically cannot see a second child, so feeding the gate from it would
     * publish "we can only see one of you" as a *measurement* rather than as the artefact of what
     * the camera was asked to look for. Silence is about not saying, never about not knowing
     * (D-075(1)).
     */
    const cooperative =
      this.maxPoses > SINGLE_BODY
        ? this.cooperativeGate.observe(
            cooperativeObservationFrom(frame, this.pose.minCoreConfidence),
          )
        : null;
    this.frameTimes = recordFrameTime(this.frameTimes, frame.detectionLatencyMs);
    this.framesObserved += 1;
    this.lastFrameAt = frame.detectedAt;
    // The watchdog is armed by the first frame and re-armed by every one after it: a session
    // nobody has started producing frames is not a disconnected camera.
    this.noteFrameArrival();
    // The frame itself goes no further than the subscribers inside this boundary; the readings
    // are all anything else ever sees.
    for (const listener of [...this.frameListeners]) listener(frame);
    return { body, cooperative };
  }

  /** An injected frame: read exactly like a detected one, so nothing downstream can tell. */
  private publishFrame(frame: PoseFrame): void {
    const { body, cooperative } = this.applyFrame(frame);
    const frameTimeMs =
      this.frameTimes.reduce((sum, value) => sum + value, 0) / this.frameTimes.length;
    this.update({ readings: { ...this.state.readings, body, cooperative, frameTimeMs } });
  }

  /**
   * Ends the camera. The engine stays warm on purpose (D-060(3)) — this is a child leaving a
   * screen, not the end of the sitting.
   */
  stop(): void {
    // Any start still in flight belongs to the screen that has just gone (S1-1). A later start is
    // a new attempt, never a coalesced ride on this one.
    this.startGeneration += 1;
    this.starting = null;
    this.cancelTick?.();
    this.cancelTick = null;
    // A camera a family turned off is not a camera that stopped working: the watchdog goes with
    // the stream, or leaving a screen would raise "camera trouble" on the way out.
    this.stopStarvationWatch();

    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    // The id goes with the stream it described. Nothing about a household's hardware outlives it.
    this.deviceId = null;
    if (this.video !== null) this.video.srcObject = null;
    this.frameTimes = [];
    // What the room looked like goes with the stream that showed it — including where two children
    // were standing in it.
    this.cooperativeGate.reset();

    this.update({
      status: 'stopped',
      streamActive: false,
      readings: {
        ...this.state.readings,
        meanLuminance: null,
        body: null,
        cooperative: null,
        frameTimeMs: null,
      },
    });
  }

  /** End of the sitting: stop the camera and release the model. */
  dispose(): void {
    this.disposed = true;
    this.frameSourceOverride?.();
    this.frameSourceOverride = null;
    this.stop();
    this.engine?.close();
    this.engine = null;
    this.listeners.clear();
    this.frameListeners.clear();
    this.starvationListeners.clear();
    // The end of the sitting is the end of every calibration taken during it (GDS §13) — and of
    // what the room looked like while it lasted.
    this.calibration.clear();
    this.lastRecommendedMode = null;
  }

  private update(patch: Partial<CameraSessionSnapshot>): void {
    this.state = { ...this.state, ...patch };
    for (const listener of this.listeners) listener();
  }
}

/**
 * The permitted scalar (S1-7): the device id of the video track, or null when the browser withholds
 * it. Nothing else is read from the track, and nothing is kept anywhere but memory.
 */
function videoDeviceIdOf(stream: MediaStream): string | null {
  try {
    const [track] = stream.getVideoTracks();
    const id = track?.getSettings?.().deviceId;
    return typeof id === 'string' && id.length > 0 ? id : null;
  } catch {
    // A track that will not describe itself is not a camera change.
    return null;
  }
}

/** Real engine, loaded on demand so keyboard-only play never downloads a WASM vision runtime. */
async function defaultEngineFactory(request: PoseEngineRequest): Promise<PoseEngineLike> {
  const { PoseLandmarkerEngine } = await import('../pose');
  return new PoseLandmarkerEngine({ runningMode: 'VIDEO', numPoses: request.maxPoses });
}

/**
 * Starts playback without letting the element's own troubles reach the session.
 *
 * A browser that refuses autoplay, or a test environment whose `play()` is not implemented, is not
 * a broken camera — the stream is attached either way, and an exception here would abandon the
 * rest of `start()` with a live stream nobody had recorded owning.
 */
function play(video: HTMLVideoElement): void {
  try {
    const started: unknown = video.play?.();
    if (started instanceof Promise) started.catch(() => {});
  } catch {
    /* nothing to do: the frames are attached, playback is the browser's business */
  }
}

/** The watchdog's own clock: an interval, because it must keep ticking with nothing to react to. */
function defaultStarvationSchedule(callback: () => void, intervalMs: number): () => void {
  const handle = setInterval(callback, intervalMs);
  return () => {
    clearInterval(handle);
  };
}

function defaultSchedule(callback: () => void, delayMs: number): () => void {
  const handle = setTimeout(callback, delayMs);
  return () => {
    clearTimeout(handle);
  };
}

/** Draws the current frame to a small offscreen canvas and averages it. */
function defaultLuminanceSampler(video: HTMLVideoElement): number | null {
  if (video.videoWidth === 0 || video.videoHeight === 0) return null;

  // Deliberately tiny: this is a room-brightness reading, not an image. Nothing is kept.
  const width = 32;
  const height = Math.max(1, Math.round((video.videoHeight / video.videoWidth) * width));
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;

  const context = canvas.getContext('2d', { willReadFrequently: true });
  if (context === null) return null;

  context.drawImage(video, 0, 0, width, height);
  return meanLuminanceFromRgba(context.getImageData(0, 0, width, height).data);
}

export type { CameraReadings, PoseEngineReadiness };
